import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import { DeliveryCostCheckPopupComponent } from '@components/invoice-form/components/services/delivery-cost-check-popup/delivery-cost-check-popup.component';
import { COST_INPUT_CONFIG } from '@shared/components/delivery-cost/delivery-cost.constants';
import { parseCostValue, redistributePlacesCost } from '@shared/components/delivery-cost/delivery-cost.helpers';
import { CargoTypes } from 'app/services/invoices/invoice-api.enums';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-delivery-cost',
  templateUrl: './delivery-cost.component.html',
  styleUrls: ['./delivery-cost.component.scss'],
})
export class DeliveryCostComponent extends AbstractFormContainer implements OnInit, OnDestroy {
  protected readonly COST_INPUT_CONFIG = COST_INPUT_CONFIG;

  @Input() controlName: string = COST_INPUT_CONFIG.controlName;

  @Input() isTemplate: boolean = false;

  showTooltip$: Observable<boolean>;

  private costChangeSubscription: Subscription;

  constructor(protected formBuilder: UntypedFormBuilder, public dialog: MatDialog) {
    super();
  }

  get backDeliveryControl(): AbstractControl | null {
    return this.form.parent.get(
      'stepAddressForm.AdditionalServices.backDelivery.BackwardDeliveryData.RedeliveryString',
    );
  }

  get afterpaymentControl(): AbstractControl | null {
    return this.form.parent.get('stepAddressForm.AdditionalServices.afterpayment.AfterpaymentOnGoodsCost');
  }

  get serviceCost(): string | number {
    return this.backDeliveryControl?.value || this.afterpaymentControl?.value;
  }

  get costControl(): AbstractControl | null {
    return this.form.get(this.controlName);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.listenShowTooltip();
    this.listenCostChange();
  }

  ngOnDestroy(): void {
    this.costChangeSubscription?.unsubscribe();
  }

  addFormControls(): void {
    const descriptionControl = new UntypedFormControl(COST_INPUT_CONFIG.initialValue, [
      ...(this.isTemplate ? [] : [Validators.required]),
      Validators.max(COST_INPUT_CONFIG.maxValue),
      Validators.min(COST_INPUT_CONFIG.minValue),
    ]);
    this.form.setControl(this.controlName, descriptionControl);
  }

  openDialog(value: string | number): void {
    const dialogRef: MatDialogRef<DeliveryCostCheckPopupComponent> = this.dialog.open(DeliveryCostCheckPopupComponent, {
      panelClass: 'no-padding',
    });

    dialogRef.disableClose = true;
    dialogRef.componentInstance.cost = value;
    dialogRef.componentInstance.announcedCost = this.costControl.value;
    if (this.backDeliveryControl) {
      dialogRef.componentInstance.type = 'BackwardDeliveryData';
    } else {
      dialogRef.componentInstance.type = null;
    }

    dialogRef.componentInstance.onAdd.subscribe(() => {
      if (+value > +this.costControl.value) {
        const previousCost = this.costControl.value;
        this.costControl.setValue(value);
        this.costControl.updateValueAndValidity({ onlySelf: true });
        redistributePlacesCost(this.form, previousCost, value);
      }
    });

    dialogRef.componentInstance.onClear.subscribe(() => {
      if (+value > +this.costControl.value) {
        const service = this.backDeliveryControl || this.afterpaymentControl;
        service.setValue(this.costControl.value);
        service.updateValueAndValidity({ onlySelf: true });
      }
    });

    dialogRef.afterClosed();
  }

  private listenCostChange(): void {
    this.costChangeSubscription = this.costControl?.valueChanges
      .pipe(debounceTime(COST_INPUT_CONFIG.debounceTime), distinctUntilChanged())
      .subscribe((value) => {
        if (this.serviceCost && Number(this.serviceCost) > parseCostValue(value)) {
          this.openDialog(this.serviceCost);
        }
      });
  }

  private listenShowTooltip(): void {
    // OptionsSeat/paramsCommonSeats are added and removed by sibling cargo-type components
    // (mounted after this one), so listen to the whole form instead of a single control.
    this.showTooltip$ = this.form.valueChanges.pipe(
      startWith(this.form.value),
      map(() => this.calculateShowTooltip()),
      distinctUntilChanged(),
    );
  }

  private calculateShowTooltip(): boolean {
    const cargoType = this.form.get('CargoType')?.value;

    if (cargoType === 'Documents') {
      return false;
    }

    if (cargoType === CargoTypes.TIRES_WHEELS) {
      const populatedSizes = this.form.get('tiresWheels')?.value?.filter((item) => Number(item.count) > 0).length || 0;
      return populatedSizes > 1;
    }

    const placesCount = this.form.get('OptionsSeat')?.value?.length || 0;

    if (cargoType === 'Cargo') {
      return !this.form.get('paramsCommonSeats')?.value && placesCount > 1;
    }

    return cargoType === 'Pallet' && placesCount > 1;
  }
}
